import Link from 'next/link';
import { ImageWithFallback } from '../figma/ImageWithFallback';
import { blogArticlePath } from '../Router';
import type { BlogPostDocument } from '../../firebase/blog-db';

interface HomeHeroProps {
  featured: BlogPostDocument | null;
}

// A default brand hero image for the masthead — always present for the
// site's visual identity, independent of whether any post has its own cover
// image set. Free-to-use stock photo (Unsplash License, no attribution
// required): https://unsplash.com/photos/n95VMLxqM2I — students working
// together on real Apple laptops, no on-screen branding/text in frame.
// w=1600/q=65 keeps this under ~200KB (Unsplash auto-negotiates WebP/AVIF
// per the browser's Accept header, so real-world delivery is smaller still)
// — this is the LCP image for the homepage, so it stays lean on purpose.
const DEFAULT_HERO_IMAGE =
  'https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=1600&q=65';

/** The blog's masthead: a giant wordmark + divider + stacked tagline, then a
 * full-bleed hero image with a bottom bar linking to the featured story. */
export function HomeHero({ featured }: HomeHeroProps) {
  // Deliberately always the fixed brand image — it's the site's visual
  // identity, not a per-article thumbnail, so it never swaps based on
  // whatever the latest/featured post's own cover image happens to be.
  const image = (
    <div className="relative aspect-[21/9] sm:aspect-[3/1] overflow-hidden bg-gray-100">
      <ImageWithFallback
        src={DEFAULT_HERO_IMAGE}
        alt="Hustlance Blog"
        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.02]"
        loading="eager"
        fetchPriority="high"
      />
    </div>
  );

  return (
    <div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-8 sm:pt-14 sm:pb-10">
        <div className="flex flex-wrap items-center gap-6 sm:gap-8">
          <h1 className="font-blocky text-6xl sm:text-8xl text-gray-900 leading-none">
            Hustlance
          </h1>
          <div className="flex items-center gap-6 sm:gap-8">
            <div className="w-px self-stretch bg-gray-200" aria-hidden="true" />
            <p className="font-blocky text-xl sm:text-2xl text-gray-900 leading-tight">
              Stories &<br />Opportunities<br />Blog
            </p>
          </div>
        </div>
      </div>

      {featured ? (
        <Link href={blogArticlePath(featured.slug)} className="group block relative">
          {image}
          <div className="bg-hustlance-primary">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <span className="font-bold text-white text-sm sm:text-base whitespace-nowrap">Featured Story:</span>
              <span className="text-white/90 text-sm sm:text-base truncate group-hover:underline">{featured.title}</span>
            </div>
          </div>
        </Link>
      ) : (
        image
      )}
    </div>
  );
}
